export type DictionaryBroadcastMessage =
  | { type: "added"; id: string; filename: string }
  | { type: "removed"; id: string; filename: string };

const CHANNEL_NAME = "fix-parser-dictionaries";

let channel: BroadcastChannel | null | undefined;

const getDictionaryChannel = (): BroadcastChannel | null => {
  if (channel !== undefined) return channel;
  if (typeof BroadcastChannel === "undefined") {
    channel = null;
    return null;
  }
  try {
    channel = new BroadcastChannel(CHANNEL_NAME);
  } catch {
    channel = null;
  }
  return channel;
};

export const postDictionaryChange = (msg: DictionaryBroadcastMessage): void => {
  const ch = getDictionaryChannel();
  if (!ch) return;
  try {
    ch.postMessage(msg);
  } catch {
    // ignore
  }
};

const STORAGE_KEY = "fix-parser-dictionaries";

export const subscribeToDictionaryChanges = (
  handler: (msg: DictionaryBroadcastMessage) => void,
): (() => void) => {
  const ch = getDictionaryChannel();
  const channelListener = (e: MessageEvent) => {
    const data = e.data as DictionaryBroadcastMessage | undefined;
    if (!data || typeof data !== "object" || !("type" in data)) return;
    if (data.type !== "added" && data.type !== "removed") return;
    handler(data);
  };
  ch?.addEventListener("message", channelListener);

  // Fallback: cross-tab `storage` events fire when another tab writes
  // localStorage. They don't carry our typed payload, so we diff the new
  // value against the old to derive add/remove messages.
  const storageListener = (e: StorageEvent) => {
    if (e.key !== STORAGE_KEY) return;
    let prev: { id: string; filename: string }[] = [];
    let next: { id: string; filename: string }[] = [];
    try {
      prev = e.oldValue ? JSON.parse(e.oldValue) : [];
      next = e.newValue ? JSON.parse(e.newValue) : [];
    } catch {
      return;
    }
    const prevIds = new Set(prev.map((d) => d.id));
    const nextIds = new Set(next.map((d) => d.id));
    for (const entry of next) {
      if (!prevIds.has(entry.id)) {
        handler({ type: "added", id: entry.id, filename: entry.filename });
      }
    }
    for (const entry of prev) {
      if (!nextIds.has(entry.id)) {
        handler({ type: "removed", id: entry.id, filename: entry.filename });
      }
    }
  };
  if (typeof window !== "undefined") {
    window.addEventListener("storage", storageListener);
  }

  return () => {
    ch?.removeEventListener("message", channelListener);
    if (typeof window !== "undefined") {
      window.removeEventListener("storage", storageListener);
    }
  };
};
